/**
 * LogoPulse Typography Configuration
 * Central typography settings using Sansation font
 */

/**
 * Font family configuration
 * Sansation with fallbacks
 */
export const fontFamily = {
  primary: '"Sansation", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
} as const;

/**
 * Font weights
 * Sansation supports: 300 (light), 400 (regular), 700 (bold)
 */
export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 400,  // Sansation doesn't have 500, using 400
  semibold: 700, // Using bold for semibold
  bold: 700,
} as const;

/**
 * Typography variant definitions
 * Used by Material-UI theme
 */
export const typography = {
  fontFamily: fontFamily.primary,

  // Headings
  h1: {
    fontFamily: fontFamily.primary,
    fontSize: '2.5rem',      // 40px
    fontWeight: fontWeight.bold,
    lineHeight: 1.2,
  },
  h2: {
    fontFamily: fontFamily.primary,
    fontSize: '2rem',        // 32px
    fontWeight: fontWeight.bold,
    lineHeight: 1.3,
  },
  h3: {
    fontFamily: fontFamily.primary,
    fontSize: '1.75rem',     // 28px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.4,
  },
  h4: {
    fontFamily: fontFamily.primary,
    fontSize: '1.5rem',      // 24px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.4,
  },
  h5: {
    fontFamily: fontFamily.primary,
    fontSize: '1.25rem',     // 20px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.5,
  },
  h6: {
    fontFamily: fontFamily.primary,
    fontSize: '1rem',        // 16px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.5,
  },

  // Body text
  body1: {
    fontFamily: fontFamily.primary,
    fontSize: '1rem',        // 16px
    fontWeight: fontWeight.regular,
    lineHeight: 1.5,
  },
  body2: {
    fontFamily: fontFamily.primary,
    fontSize: '0.875rem',    // 14px
    fontWeight: fontWeight.regular,
    lineHeight: 1.5,
  },

  // UI Elements
  button: {
    fontFamily: fontFamily.primary,
    fontSize: '0.875rem',    // 14px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.75,
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: fontFamily.primary,
    fontSize: '0.75rem',     // 12px
    fontWeight: fontWeight.regular,
    lineHeight: 1.66,
  },
  overline: {
    fontFamily: fontFamily.primary,
    fontSize: '0.75rem',     // 12px
    fontWeight: fontWeight.semibold,
    lineHeight: 2.66,
    textTransform: 'uppercase' as const,
  },

  // Subtitle variants
  subtitle1: {
    fontFamily: fontFamily.primary,
    fontSize: '1rem',        // 16px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.75,
  },
  subtitle2: {
    fontFamily: fontFamily.primary,
    fontSize: '0.875rem',    // 14px
    fontWeight: fontWeight.semibold,
    lineHeight: 1.57,
  },
} as const;

export type Typography = typeof typography;
export type FontFamily = typeof fontFamily;
export type FontWeight = typeof fontWeight;
